import { useMemo, useState, useCallback } from 'react';
import {
    ShoppingCart,
    DollarSign,
    Package,
    CreditCard,
    Wallet,
    TrendingUp,
    PieChart,
    BarChart3,
    Percent,
    Calculator,
    Banknote,
    Scale,
    Building2,
    Store,
    MapPin,
    Sparkles
} from 'lucide-react';
import {
    ResponsiveContainer,
    PieChart as RechartsPie,
    Pie,
    Cell,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ComposedChart,
    Line,
    Bar,
} from 'recharts';


import KPICard from '../components/common/KPICard';
import ChartCard from '../components/common/ChartCard';
import DataTable from '../components/common/DataTable';
import InsightCard from '../components/common/InsightCard';
import BestSellersByArea from '../components/common/BestSellersByArea';
import { useFilters } from '../context/FilterContext';
import { applyMultiLevelFilters } from '../utils/filters';
import { formatCurrency, formatNumber, formatDate, formatTimeSlot } from '../utils/formatters';
import { aggregateByTimePeriod, calculateGrowth } from '../utils/calculations';
import {
    getTopCitiesByGMV,
    getTopStoresByGMV,
    getTopPincodesByGMV,
    getCODPercentageByPincode,
    getTopProductsInScope,
    getScopeLabel,
} from '../utils/insights';
import styles from './OrderAnalytics.module.css';

function OrderAnalytics({ onNavigateToTab }) {
    const { appliedFilters, orders, setCities, setStores, setPincodes, applyFilters } = useFilters();
    const [bestSellersSort, setBestSellersSort] = useState('value');

    // Filter orders based on applied filters
    const filteredOrders = useMemo(() => {
        const filters = {
            startDate: appliedFilters.dateRange?.startDate
                ? appliedFilters.dateRange.startDate.toISOString().split('T')[0]
                : null,
            endDate: appliedFilters.dateRange?.endDate
                ? appliedFilters.dateRange.endDate.toISOString().split('T')[0]
                : null,
            cities: appliedFilters.cities,
            stores: appliedFilters.stores,
            pincodes: appliedFilters.pincodes,
        };

        return applyMultiLevelFilters(orders, filters);
    }, [appliedFilters]);

    // Calculate KPI metrics
    const metrics = useMemo(() => {
        const totalOrders = filteredOrders.length;
        const gmv = filteredOrders.reduce((sum, o) => sum + o.total, 0);
        const discountValue = filteredOrders.reduce((sum, o) => sum + o.discount, 0);
        const volume = filteredOrders.reduce((sum, o) => sum + o.itemCount, 0);
        const avgOrderValue = totalOrders > 0 ? gmv / totalOrders : 0;
        const avgOrderVolume = totalOrders > 0 ? volume / totalOrders : 0;
        const onlineOrders = filteredOrders.filter(o => o.paymentMethod === 'Online').length;
        const codOrders = filteredOrders.filter(o => o.paymentMethod === 'COD').length;
        const onlineGMV = filteredOrders.filter(o => o.paymentMethod === 'Online').reduce((sum, o) => sum + o.total, 0);
        const codGMV = filteredOrders.filter(o => o.paymentMethod === 'COD').reduce((sum, o) => sum + o.total, 0);

        // Calculate trend (compare to previous period - simplified)
        const midpoint = Math.floor(filteredOrders.length / 2);
        const recentOrders = filteredOrders.slice(midpoint);
        const olderOrders = filteredOrders.slice(0, midpoint);

        const recentGMV = recentOrders.reduce((sum, o) => sum + o.total, 0);
        const olderGMV = olderOrders.reduce((sum, o) => sum + o.total, 0);
        const gmvTrend = calculateGrowth(recentGMV, olderGMV);

        return {
            totalOrders,
            gmv,
            discountValue,
            volume,
            avgOrderValue,
            avgOrderVolume,
            onlineOrders,
            codOrders,
            onlineGMV,
            codGMV,
            gmvTrend,
        };
    }, [filteredOrders]);

    // ========== INSIGHT DATA ==========
    const topCities = useMemo(() => getTopCitiesByGMV(filteredOrders, 1), [filteredOrders]);
    const topStores = useMemo(() => getTopStoresByGMV(filteredOrders, 1), [filteredOrders]);
    const topPincodes = useMemo(() => getTopPincodesByGMV(filteredOrders, 1), [filteredOrders]);
    const highestCODPincodes = useMemo(() => getCODPercentageByPincode(filteredOrders, 1), [filteredOrders]);

    const scopeLabel = useMemo(() => getScopeLabel(appliedFilters, orders), [appliedFilters, orders]);

    const topProductsByValue = useMemo(() =>
        getTopProductsInScope(filteredOrders, 5, 'value'),
        [filteredOrders]
    );
    const topProductsByVolume = useMemo(() =>
        getTopProductsInScope(filteredOrders, 5, 'volume'),
        [filteredOrders]
    );

    // Navigation handlers
    const handleViewCityOrders = useCallback(async () => {
        if (topCities.length > 0) {
            setCities([topCities[0].id]);
            await applyFilters();
        }
    }, [topCities, setCities, applyFilters]);

    const handleViewStoreOrders = useCallback(async () => {
        if (topStores.length > 0) {
            setStores([topStores[0].id]);
            await applyFilters();
        }
    }, [topStores, setStores, applyFilters]);

    const handleViewPincodeOrders = useCallback(async () => {
        if (topPincodes.length > 0) {
            setPincodes([topPincodes[0].code]);
            await applyFilters();
        }
    }, [topPincodes, setPincodes, applyFilters]);

    const handleOpenSalesMix = useCallback(() => {
        if (onNavigateToTab) onNavigateToTab('sales-mix');
    }, [onNavigateToTab]);

    // Generate sparkline data
    const sparklineData = useMemo(() => {
        const daily = aggregateByTimePeriod(filteredOrders, 'day');
        return daily.slice(-14).map(d => ({ value: d.total }));
    }, [filteredOrders]);

    // Payment distribution for pie chart
    const paymentDistribution = useMemo(() => [
        { name: 'Online', value: metrics.onlineOrders, color: '#50C878' },
        { name: 'COD', value: metrics.codOrders, color: '#FFB347' },
    ], [metrics]);

    // Payment trend data for area chart
    const paymentTrendData = useMemo(() => {
        const daily = aggregateByTimePeriod(filteredOrders, 'day');
        return daily.slice(-30).map(d => {
            const online = d.items.filter(o => o.paymentMethod === 'Online').reduce((s, o) => s + o.total, 0);
            const cod = d.items.filter(o => o.paymentMethod === 'COD').reduce((s, o) => s + o.total, 0);
            return {
                date: formatDate(d.period, 'short'),
                Online: online,
                COD: cod,
            };
        });
    }, [filteredOrders]);

    // Revenue & Volume trend data
    const gmvVolumeTrendData = useMemo(() => {
        const daily = aggregateByTimePeriod(filteredOrders, 'day');
        return daily.slice(-30).map(d => ({
            date: formatDate(d.period, 'short'),
            Revenue: d.total,
            Volume: d.items.reduce((sum, o) => sum + o.itemCount, 0),
        }));
    }, [filteredOrders]);

    // Table columns
    const tableColumns = [
        { key: 'date', label: 'Date', render: (v) => formatDate(v, 'medium') },
        { key: 'id', label: 'Order ID', render: (v) => v.replace('ORD-', '#') },
        { key: 'storeName', label: 'Store' },
        { key: 'cityId', label: 'City', render: (v) => v.replace('CITY-', '') },
        { key: 'pincode', label: 'Pin Code' },
        { key: 'total', label: 'Revenue', render: (v) => formatCurrency(v) },
        { key: 'discount', label: 'Discount', render: (v) => formatCurrency(v) },
        { key: 'itemCount', label: 'Volume' },
        { key: 'paymentMethod', label: 'Payment' },
        { key: 'total', label: 'Order Value', render: (_, row) => formatCurrency((row.total || 0) - (row.deliveryFee || 0)) },
        { key: 'deliveryFee', label: 'Delivery Charges', render: (v) => formatCurrency(v || 0) },
        { key: 'deliveryScheduleDate', label: 'Schedule Date', render: (v) => v ? formatDate(v, 'medium') : '-' },
        { key: 'timeSlotId', label: 'Time Slot', render: (v, row) => formatTimeSlot(row._original?.TimeSlotId || v) },
        { key: 'customerOrderDate', label: 'Customer Order Date', render: (v) => v ? formatDate(v, 'medium') : '-' },
    ];

    // Export generator for item-wise Excel data
    const generateExportData = useCallback((dataToExport) => {
        const exportedRows = [];
        dataToExport.forEach(order => {
            const baseRow = {
                'CustomerOrderDate': order.customerOrderDate ? order.customerOrderDate : '',
                'OrderDate': order.orderDateRaw ? order.orderDateRaw : '',
                'DeliveryScheduleDate': order.deliveryScheduleDate ? order.deliveryScheduleDate : '',
                'TimeSlot': formatTimeSlot(order._original?.TimeSlotId || order.timeSlotId),
                'Order ID': order.id,
                'Store Name': order.storeName,
                'City': order.cityName,
                'Pin Code': order.pincode,
                'Payment Method': order.paymentMethod,
                'Order Value': order.total,
                'Discount': order.discount,
                'Delivery Fee': order.deliveryFee,
            };

            const skuDetails = order._original?.OrderSkuDetails;
            if (Array.isArray(skuDetails) && skuDetails.length > 0) {
                skuDetails.forEach(item => {
                    exportedRows.push({
                        ...baseRow,
                        'LineNumber': item.lineNumber ?? item.LineNumber ?? '',
                        'ItemId': item.itemId || item.ItemId || '',
                        'IsSet': item.isSet ?? item.IsSet ?? '',
                        'IsOfferItem': item.isOfferItem ?? item.IsOfferItem ?? '',
                        'ItemRate': item.itemRate ?? item.ItemRate ?? '',
                        'Quantity': item.quantity ?? item.Quantity ?? '',
                        'Rate': item.rate ?? item.Rate ?? '',
                        'ProductAmount': item.productAmount ?? item.ProductAmount ?? '',
                        'TotalAmount': item.totalAmount ?? item.TotalAmount ?? '',
                        'DeliveryCharge': item.deliveryCharge ?? item.DeliveryCharge ?? '',
                        'SkuId': item.skuId || item.SkuId || '',
                        'SkuName': item.skuName || item.SkuName || '',
                        'ItemName': item.itemName || item.ItemName || '',
                        'CategoryName': item.categoryName || item.CategoryName || '',
                        'Division': item.division || item.Division || '',
                        'ItemFamilyID': item.itemFamilyID || item.ItemFamilyID || ''
                    });
                });
            } else {
                exportedRows.push(baseRow);
            }
        });
        return exportedRows;
    }, []);

    const exportColumns = [
        'Order ID', 'Store Name', 'City', 'Pin Code', 'CustomerOrderDate', 'OrderDate',
        'DeliveryScheduleDate', 'TimeSlot', 'Payment Method', 'Order Value', 'Discount', 'Delivery Fee',
        'LineNumber', 'ItemId', 'IsSet', 'IsOfferItem', 'ItemRate', 'Quantity', 'Rate',
        'ProductAmount', 'TotalAmount', 'DeliveryCharge', 'SkuId', 'SkuName', 'ItemName',
        'CategoryName', 'Division', 'ItemFamilyID'
    ];

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <h1 className={styles.title}>Order Analytics</h1>
                <p className={styles.subtitle}>Track and analyze your order performance metrics</p>
            </header>



            {/* KPI Cards - 3x3 Grid */}
            <div className={styles.kpiGrid}>
                <KPICard
                    title="Revenue"
                    value={formatCurrency(metrics.gmv, { compact: true })}
                    trend={metrics.gmvTrend}
                    icon={DollarSign}
                    color="success"
                    sparklineData={sparklineData}
                />
                <KPICard
                    title="Coupon Discount Value"
                    value={formatCurrency(metrics.discountValue, { compact: true })}
                    trend={-5.2}
                    icon={Percent}
                    color="warning"
                />
                <KPICard
                    title="Volume (Items)"
                    value={formatNumber(metrics.volume, { compact: true })}
                    trend={8.5}
                    icon={Package}
                    color="primary"
                />
                <KPICard
                    title="Total Orders"
                    value={formatNumber(metrics.totalOrders)}
                    trend={12.3}
                    icon={ShoppingCart}
                    color="secondary"
                />
                <KPICard
                    title="Avg Order Value"
                    value={formatCurrency(metrics.avgOrderValue)}
                    trend={3.7}
                    icon={Calculator}
                    color="info"
                />
                <KPICard
                    title="Avg Order Volume"
                    value={metrics.avgOrderVolume.toFixed(1)}
                    trend={2.1}
                    icon={Scale}
                    color="primary"
                />
                <KPICard
                    title="Online Payments"
                    value={formatNumber(metrics.onlineOrders)}
                    trend={15.8}
                    icon={CreditCard}
                    color="success"
                />
                <KPICard
                    title="COD Payments"
                    value={formatNumber(metrics.codOrders)}
                    trend={-2.4}
                    icon={Banknote}
                    color="warning"
                />
                {/* Summary Card */}
            </div>

            {/* ========== INSIGHTS STRIP ========== */}
            <section className={styles.insightsStrip}>
                <div className={styles.insightsHeader}>
                    <Sparkles size={18} />
                    <span>Quick Insights</span>
                </div>
                <div className={styles.insightsGrid}>
                    {topCities.length > 0 && (
                        <InsightCard
                            title="Best City"
                            icon={Building2}
                            primaryLabel="by Revenue"
                            primaryValue={topCities[0].name}
                            secondaryStats={[
                                { label: 'Revenue', value: formatCurrency(topCities[0].gmv, { compact: true }) },
                            ]}
                            onViewDetails={handleViewCityOrders}
                            viewDetailsLabel="Filter"
                            color="primary"
                            compact
                        />
                    )}

                    {topStores.length > 0 && (
                        <InsightCard
                            title="Best Store"
                            icon={Store}
                            primaryLabel="by Revenue"
                            primaryValue={topStores[0].name}
                            secondaryStats={[
                                { label: 'Revenue', value: formatCurrency(topStores[0].gmv, { compact: true }) },
                            ]}
                            onViewDetails={handleViewStoreOrders}
                            viewDetailsLabel="Filter"
                            color="success"
                            compact
                        />
                    )}

                    {topPincodes.length > 0 && (
                        <InsightCard
                            title="Best Pincode"
                            icon={MapPin}
                            primaryLabel="by Revenue"
                            primaryValue={topPincodes[0].code}
                            secondaryStats={[
                                { label: 'Revenue', value: formatCurrency(topPincodes[0].gmv, { compact: true }) },
                            ]}
                            onViewDetails={handleViewPincodeOrders}
                            viewDetailsLabel="Filter"
                            color="secondary"
                            compact
                        />
                    )}

                    {highestCODPincodes.length > 0 && (
                        <InsightCard
                            title="Highest COD%"
                            icon={Banknote}
                            primaryLabel="Pincode"
                            primaryValue={highestCODPincodes[0].code}
                            secondaryStats={[
                                { label: 'COD%', value: `${highestCODPincodes[0].codPercent.toFixed(1)}%` },
                            ]}
                            color="warning"
                            compact
                        />
                    )}
                </div>
            </section>

            {/* Best Sellers in Current Scope */}
            {(topProductsByValue.length > 0 || topProductsByVolume.length > 0) && (
                <section className={styles.bestSellersSection}>
                    <BestSellersByArea
                        title="Best Sellers in Current Scope"
                        scopeLabel={scopeLabel}
                        topByValueRows={topProductsByValue}
                        topByVolumeRows={topProductsByVolume}
                        onNavigateSalesMix={handleOpenSalesMix}
                    />
                </section>
            )}

            {/* Payment Analysis Section */}
            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>
                    <Wallet size={20} className={styles.sectionIcon} />
                    Payment Analysis
                </h2>

                {/* Payment Cards */}
                <div className={styles.paymentCardsGrid}>
                    <KPICard
                        title="Online Payment Revenue"
                        value={formatCurrency(metrics.onlineGMV, { compact: true })}
                        trend={18.5}
                        icon={CreditCard}
                        color="success"
                        compact
                    />
                    <KPICard
                        title="COD Payment Revenue"
                        value={formatCurrency(metrics.codGMV, { compact: true })}
                        trend={-3.2}
                        icon={Banknote}
                        color="warning"
                        compact
                    />
                </div>

                <div className={styles.chartsGrid}>
                    {/* Donut Chart */}
                    <ChartCard
                        title="Payment Distribution"
                        icon={PieChart}
                        height={280}
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <RechartsPie>
                                <Pie
                                    data={paymentDistribution}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={100}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {paymentDistribution.map((entry, index) => (
                                        <Cell key={index} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    formatter={(value) => formatNumber(value)}
                                    contentStyle={{
                                        backgroundColor: 'var(--color-card)',
                                        border: '1px solid var(--color-border)',
                                        borderRadius: '8px',
                                    }}
                                />
                                <Legend />
                            </RechartsPie>
                        </ResponsiveContainer>
                    </ChartCard>

                    {/* Stacked Area Chart */}
                    <ChartCard
                        title="Payment Trends (30 Days)"
                        icon={BarChart3}
                        height={280}
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={paymentTrendData}>
                                <defs>
                                    <linearGradient id="colorOnline" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#50C878" stopOpacity={0.8} />
                                        <stop offset="95%" stopColor="#50C878" stopOpacity={0.1} />
                                    </linearGradient>
                                    <linearGradient id="colorCOD" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#FFB347" stopOpacity={0.8} />
                                        <stop offset="95%" stopColor="#FFB347" stopOpacity={0.1} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                                <XAxis
                                    dataKey="date"
                                    tick={{ fontSize: 11 }}
                                    tickLine={false}
                                    axisLine={{ stroke: 'var(--color-border)' }}
                                />
                                <YAxis
                                    tick={{ fontSize: 11 }}
                                    tickLine={false}
                                    axisLine={false}
                                    tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}K`}
                                />
                                <Tooltip
                                    formatter={(value) => formatCurrency(value)}
                                    contentStyle={{
                                        backgroundColor: 'var(--color-card)',
                                        border: '1px solid var(--color-border)',
                                        borderRadius: '8px',
                                    }}
                                />
                                <Legend />
                                <Area
                                    type="monotone"
                                    dataKey="Online"
                                    stackId="1"
                                    stroke="#50C878"
                                    fill="url(#colorOnline)"
                                />
                                <Area
                                    type="monotone"
                                    dataKey="COD"
                                    stackId="1"
                                    stroke="#FFB347"
                                    fill="url(#colorCOD)"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>
            </section>

            {/* Revenue & Volume Trend Section */}
            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>
                    <TrendingUp size={20} className={styles.sectionIcon} />
                    Revenue & Volume Trend
                </h2>

                <ChartCard
                    title="Daily Revenue & Volume (30 Days)"
                    icon={BarChart3}
                    height={320}
                >
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={gmvVolumeTrendData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                            <XAxis
                                dataKey="date"
                                tick={{ fontSize: 11 }}
                                tickLine={false}
                                axisLine={{ stroke: 'var(--color-border)' }}
                            />
                            <YAxis
                                yAxisId="left"
                                tick={{ fontSize: 11 }}
                                tickLine={false}
                                axisLine={false}
                                tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}K`}
                            />
                            <YAxis
                                yAxisId="right"
                                orientation="right"
                                tick={{ fontSize: 11 }}
                                tickLine={false}
                                axisLine={false}
                            />
                            <Tooltip
                                formatter={(value, name) => [
                                    name === 'Revenue' ? formatCurrency(value) : formatNumber(value),
                                    name
                                ]}
                                contentStyle={{
                                    backgroundColor: 'var(--color-card)',
                                    border: '1px solid var(--color-border)',
                                    borderRadius: '8px',
                                }}
                            />
                            <Legend />
                            <Bar
                                yAxisId="right"
                                dataKey="Volume"
                                fill="rgba(74, 144, 226, 0.3)"
                                radius={[4, 4, 0, 0]}
                            />
                             <Line
                                yAxisId="left"
                                type="monotone"
                                dataKey="Revenue"
                                stroke="#50C878"
                                strokeWidth={3}
                                dot={false}
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                </ChartCard>
            </section>

            {/* Detailed Data Table */}
            <section className={styles.section}>
                <DataTable
                    data={filteredOrders}
                    columns={tableColumns}
                    title="Order Details"
                    icon={ShoppingCart}
                    exportFilename="orders"
                    exportData={generateExportData}
                    exportColumns={exportColumns}
                    defaultPageSize={10}
                />
            </section>
        </div>
    );
}

export default OrderAnalytics;
